import React, { useState, useEffect, useRef } from 'react';
import { withRouter } from 'react-router';
import Button from '../button/Button';
import Toaster from '../common/Toaster';
import Api from '../../helper/Api';
import { compose } from 'redux';
import { connect } from 'react-redux';
import './AdvertisementForm.css';

const AdvertisementForm = (props) => {
	const { match } = props;
	const isEdit = match.params.id ? true : false;
	const adId = match.params.id;

	const [formData, setFormData] = useState({
		title: '',
		description: '',
		mediaType: 'image',
		isActive: true,
		displayDuration: 10, // Default countdown for images in seconds
	});
	const [mediaFile, setMediaFile] = useState(null);
	const [mediaPreview, setMediaPreview] = useState(null);
	const [events, setEvents] = useState([]);
	const [selectedEvents, setSelectedEvents] = useState([]);
	const [availableEvents, setAvailableEvents] = useState([]);
	const [loader, setLoader] = useState(false);
	const toaster = useRef();

	useEffect(() => {
		loadEvents();
	}, []);

	useEffect(() => {
		if (isEdit && events.length > 0) {
			loadAdvertisement();
		}
	}, [isEdit, adId, events.length]);

	const loadEvents = async () => {
		try {
			let allEvents = [];
			let currentPage = 1;
			let hasMoreData = true;

			// Loop through all pages to get all event data
			while (hasMoreData) {
				const params = {
					sort_by: 'createdAt',
					order: 'DESC',
					search_event: '',
					search_user: '',
					page: currentPage.toString(),
				};

				const res = await Api('POST', 'cms/event/list', params);
				
				if (res.status === 200) {
					const pageData = res.data.data.rows || [];
					allEvents = [...allEvents, ...pageData];

					// Check if there's more data available
					const totalCount = res.data.data.count || 0;
					const loadedCount = allEvents.length;
					
					if (loadedCount >= totalCount || pageData.length === 0) {
						hasMoreData = false;
					} else {
						currentPage++;
					}
				} else {
					hasMoreData = false;
				}
			}

		// Ensure allEvents is an array before setting state
		const safeEvents = Array.isArray(allEvents) ? allEvents : [];
		setEvents(safeEvents);
		setAvailableEvents(safeEvents);
		} catch (err) {
			console.error('Error loading events:', err);
		}
	};

	const loadAdvertisement = () => {
		setLoader(true);
		console.log('Loading advertisement with ID:', adId);
		Api('GET', `cms/advertisement/${adId}`)
			.then((res) => {
				console.log('=== ADVERTISEMENT LOAD DEBUG ===');
				console.log('Full API response:', res);
				console.log('Response data:', res.data);
				console.log('Response data keys:', Object.keys(res.data || {}));
				if (res.status === 200) {
					const ad = res.data.data || res.data; // Handle both response structures
					console.log('Advertisement data:', ad);
					console.log('Advertisement data keys:', Object.keys(ad || {}));
					console.log('Looking for events in ad.associatedEvents:', ad.associatedEvents);
					console.log('Looking for events in ad.events:', ad.events);
					console.log('Looking for events in ad.eventIds:', ad.eventIds);
					setFormData({
						title: ad.title || '',
						description: ad.description || '',
						mediaType: ad.mediaType || 'image',
						isActive: ad.isActive !== undefined ? ad.isActive : true,
						displayDuration: ad.displayDuration || 10, // Load existing countdown or default
					});
					setMediaPreview(ad.fileUrl || ad.mediaUrl); // Try both field names
					
					// Load associated events if available - check multiple possible field names
					let associatedEvents = ad.associatedEvents || ad.events || [];
					
					// If we have event IDs but not event objects, find the event objects
					if (ad.eventIds && Array.isArray(ad.eventIds) && ad.eventIds.length > 0) {
						console.log('Found eventIds:', ad.eventIds);
						associatedEvents = events.filter(evt => ad.eventIds.includes(evt.id));
						console.log('Matched events from eventIds:', associatedEvents);
					}
					
					console.log('Final associatedEvents:', associatedEvents);
					
					if (associatedEvents && Array.isArray(associatedEvents) && associatedEvents.length > 0) {
						setSelectedEvents(associatedEvents);
						// Remove selected events from available events
						const selectedEventIds = associatedEvents.map(evt => evt.id);
						setAvailableEvents(events.filter(evt => !selectedEventIds.includes(evt.id)));
					}
					setLoader(false);
				} else {
					setLoader(false);
					if (toaster.current) toaster.current.error(res.data.message || 'Failed to load advertisement');
				}
			})
			.catch((err) => {
				console.error('Error loading advertisement:', err);
				setLoader(false);
				if (toaster.current) toaster.current.error('Failed to load advertisement');
			});
	};

	const handleInputChange = (e) => {
		const { name, value, type, checked } = e.target;
		setFormData({
			...formData,
			[name]: type === 'checkbox' ? checked : value,
		});
	};

	const handleFileChange = (e) => {
		const file = e.target.files[0];
		if (file) {
			// Check file size limit (32MB = 33554432 bytes)
			const maxSizeInBytes = 32 * 1024 * 1024; // 32MB
			if (file.size > maxSizeInBytes) {
				if (toaster.current) {
					toaster.current.error(
						`File size too large. Maximum allowed size is 32MB. Your file is ${(file.size / (1024 * 1024)).toFixed(1)}MB.`
					);
				}
				// Clear the input
				e.target.value = '';
				return;
			}

			setMediaFile(file);
			const reader = new FileReader();
			reader.onload = () => {
				setMediaPreview(reader.result);
			};
			reader.readAsDataURL(file);

			// Auto-detect media type
			if (file.type.startsWith('image/')) {
				setFormData({ ...formData, mediaType: 'image' });
			} else if (file.type.startsWith('video/')) {
				setFormData({ ...formData, mediaType: 'video' });

				// Get video duration
				const videoElement = document.createElement('video');
				videoElement.src = URL.createObjectURL(file);
				videoElement.addEventListener('loadedmetadata', () => {
					setFormData((prevData) => ({ ...prevData, duration: videoElement.duration }));
				});
			}
		}
	};

	const handleEventSelect = (e) => {
		const eventId = parseInt(e.target.value);
		const event = events.find((evt) => evt.id === eventId);
		if (event && !selectedEvents.find((evt) => evt.id === eventId)) {
			setSelectedEvents([...selectedEvents, event]);
			setAvailableEvents(availableEvents.filter((evt) => evt.id !== eventId));
		}
	};

	const removeEvent = (eventId) => {
		const event = selectedEvents.find((evt) => evt.id === eventId);
		setSelectedEvents(selectedEvents.filter((evt) => evt.id !== eventId));
		if (event) {
			setAvailableEvents([...availableEvents, event]);
		}
	};

	const handleSubmit = (e) => {
		e.preventDefault();
		setLoader(true);

		console.log('=== FORM SUBMISSION START ===');
		console.log('Form data:', formData);
		console.log('Media file:', mediaFile ? {
			name: mediaFile.name,
			size: mediaFile.size,
			type: mediaFile.type,
			lastModified: mediaFile.lastModified
		} : 'No file selected');
		console.log('Selected Events:', selectedEvents);
		console.log('Selected Event IDs:', selectedEvents.map((evt) => evt.id));
		console.log('Events JSON String:', JSON.stringify(selectedEvents.map((evt) => evt.id)));
		console.log('Is Edit Mode:', isEdit);
		console.log('Advertisement ID:', adId);

		const submitData = new FormData();
		submitData.append('title', formData.title);
		submitData.append('description', formData.description);
		submitData.append('mediaType', formData.mediaType);
		submitData.append('isActive', formData.isActive);
		submitData.append('events', JSON.stringify(selectedEvents.map((evt) => evt.id)));
		
		// Add display duration for image media
		if (formData.mediaType === 'image') {
			submitData.append('displayDuration', formData.displayDuration);
			console.log('Added image display duration:', formData.displayDuration);
		}

		// Add duration for video media
		if (formData.mediaType === 'video' && formData.duration) {
			submitData.append('duration', formData.duration);
			console.log('Added video duration:', formData.duration);
		}

		if (mediaFile) {
			submitData.append('media', mediaFile);
			console.log('Added media file to FormData');
		} else if (!isEdit) {
			console.error('No media file provided for new advertisement');
			setLoader(false);
			if (toaster.current) toaster.current.error('Please select a media file');
			return;
		}

		const endpoint = isEdit ? `cms/advertisement/${adId}` : 'cms/advertisement';
		const method = isEdit ? 'PUT' : 'POST';
		
		console.log('API Endpoint:', endpoint);
		console.log('HTTP Method:', method);
		console.log('Submitting form data...');

		Api(method, endpoint, submitData)
			.then((res) => {
				if (res.status === 200 || res.status === 201) {
					setLoader(false);
					if (toaster.current) {
						toaster.current.success(
							isEdit ? 'Advertisement updated successfully' : 'Advertisement created successfully'
						);
					}
					setTimeout(() => {
						props.history.push('/advertisement');
					}, 1500);
				} else {
					setLoader(false);
					if (toaster.current) toaster.current.error(res.data.message || 'Operation failed');
				}
			})
			.catch((err) => {
				setLoader(false);
				if (toaster.current) toaster.current.error('Operation failed');
			});
	};

	return (
		<div className="plr-15">
			<div className="roe-card-style mtb-15">
				<div className="roe-card-header module-header">
					<div className="flex-1 fs-16 demi-bold-text">
						{isEdit ? 'Edit Advertisement' : 'Add Advertisement'}
					</div>
				</div>
				<div className="roe-card-body">
					<form onSubmit={handleSubmit} className="advertisement-form">
						<div className="form-group">
							<label>Title *</label>
							<input
								type="text"
								name="title"
								value={formData.title}
								onChange={handleInputChange}
								className="form-control"
								required
							/>
						</div>

						<div className="form-group">
							<label>Description</label>
							<textarea
								name="description"
								value={formData.description}
								onChange={handleInputChange}
								className="form-control"
								rows="3"
							/>
						</div>

						<div className="form-group">
							<label>Media Type</label>
							<select
								name="mediaType"
								value={formData.mediaType}
								onChange={handleInputChange}
								className="form-control"
							>
								<option value="image">Image</option>
								<option value="video">Video</option>
							</select>
						</div>

						<div className="form-group">
							<label>Media File {!isEdit && '*'}</label>
							<input
								type="file"
								onChange={handleFileChange}
								className="form-control"
								accept={formData.mediaType === 'image' ? 'image/*' : 'video/*'}
								required={!isEdit}
							/>
							<small className="form-text text-muted" style={{ color: '#dc3545', fontWeight: 'bold' }}>
								⚠️ Maximum file size: 32MB. Larger files will be rejected.
							</small>
							{mediaPreview && (
								<div className="media-preview">
									{formData.mediaType === 'image' ? (
										<img src={mediaPreview} alt="Preview" />
									) : (
										<video 
											controls 
											src={mediaPreview}
											style={{ maxWidth: '100%', height: 'auto' }}
										>
											Your browser does not support video playback.
										</video>
									)}
								</div>
							)}
						</div>

						{/* Display Duration for Images */}
						{formData.mediaType === 'image' && (
							<div className="form-group">
								<label>Image Display Duration ({formData.displayDuration} seconds)</label>
								<div className="duration-control">
									<input
										type="range"
										min="5"
										max="30"
										value={formData.displayDuration}
										onChange={(e) => setFormData({
											...formData,
											displayDuration: parseInt(e.target.value)
										})}
										className="form-range"
										style={{ width: '70%', marginRight: '10px' }}
									/>
									<input
										type="number"
										min="5"
										max="30"
										value={formData.displayDuration}
										onChange={(e) => {
											const value = Math.max(5, Math.min(30, parseInt(e.target.value) || 10));
											setFormData({
												...formData,
												displayDuration: value
											});
										}}
										className="form-control"
										style={{ width: '80px', display: 'inline-block' }}
									/>
								</div>
								<small className="form-text text-muted">
									Set the countdown duration for image advertisements (5-30 seconds)
								</small>
							</div>
						)}

						<div className="form-group">
							<label>Associate with Events</label>
							<select onChange={handleEventSelect} className="form-control" value="">
								<option value="">Select an event to add</option>
								{availableEvents.map((event) => (
									<option key={event.id} value={event.id}>
										{event.eventName}
									</option>
								))}
							</select>
							<div className="event-associations">
								{selectedEvents.map((event) => (
									<div key={event.id} className="event-item">
										<span className="event-name">{event.eventName}</span>
										<button
											type="button"
											className="remove-btn"
											onClick={() => removeEvent(event.id)}
										>
											<i className="fas fa-times" />
										</button>
									</div>
								))}
							</div>
						</div>

						<div className="form-group">
							<label>
								<input
									type="checkbox"
									name="isActive"
									checked={formData.isActive}
									onChange={handleInputChange}
								/>{' '}
								Active
							</label>
						</div>

						<div className="form-group">
							<Button
								type="submit"
								className="c-btn c-success mr-10"
								disabled={loader}
							>
								{loader ? 'Saving...' : isEdit ? 'Update' : 'Create'}
							</Button>
							<Button
								type="button"
								className="c-btn c-secondary"
								onClick={() => props.history.push('/advertisement')}
							>
								Cancel
							</Button>
						</div>
					</form>
				</div>
			</div>
			<Toaster ref={toaster} />
		</div>
	);
};

export default compose(withRouter, connect(null, null))(AdvertisementForm);
